/**
 * FirstPersonController: fly-style first-person controller.
 *
 * Adapted from supersplat-viewer/src/cameras/fly-controller.ts.
 * WASD moves along the camera basis (including pitch), E/Q (or Space) ascend/descend,
 * mouse controls yaw/pitch via pointer lock, sphere collision with sliding.
 * No gravity — free roam. Falls back to free flight when no collision is present.
 *
 * `move` input is a normalized-ish direction vector [strafe, up, forward]
 * (already scaled by the input layer for Shift/Ctrl); this controller multiplies
 * by `moveSpeed * dt` to produce a displacement.
 */

import { Vec3 } from 'playcanvas';

import type { Collision } from '../collision';
import { findSphereSpawn } from '../collision';
import type { CameraFrame, CameraState, CameraController } from './types';
import { DEFAULT_CONTROLLER_DAMPING, applyFrameRotation, dampAngles, setBasisOffset, setCameraBasis } from './camera-utils';
import { SpawnState } from './spawn-state';
import { SphereMover } from './sphere-mover';

/** Radius of the camera collision sphere (meters). */
const CAMERA_RADIUS = 0.2;

/** 飞行贴地钳制：向下探针的起点余量与判定皮肤（米）。 */
const FLOOR_PROBE_MARGIN = 0.5;
const FLOOR_PROBE_SKIN = 0.1;

const forward = new Vec3();
const right = new Vec3();
const up = new Vec3();
const offset = new Vec3();
const spawnProbe = new Vec3();

class FirstPersonController implements CameraController {
    fov = 75;

    /** Movement speed in m/s. */
    moveSpeed = 4;

    /** Maximum height (meters) the player can ascend above the spawn point. */
    flyMaxHeight = 4;

    /**
     * 进场飞行待碰撞期（walk 默认场景、collision.glb 未到）：把相机高度钳在
     * 进场落点（场景设定的初始位置高度），E/Q 与俯仰前飞带来的垂直位移全部
     * 抵消——无碰撞数据时不允许钻到地面以下或悬空漂移；碰撞到货切行走时也
     * 不会有高度跳变。由 main.ts 在进场/碰撞安装/编辑器进出时切换。
     */
    holdEntryAltitude = false;

    /**
     * 飞行贴地钳制：眼睛位置到脚底的垂直距离（avatar eyeHeight + groundOffset）。
     * 飞行下压时脚底最多贴到地表、不陷入地面；0 = 禁用（编辑器自由飞）。
     * 由 main.ts 在形象加载/切换时同步。
     */
    flyFeetDrop = 1.5;

    rotateDamping = DEFAULT_CONTROLLER_DAMPING;

    private _position = new Vec3();
    private _angles = new Vec3();
    private _targetAngles = new Vec3();
    private _spawn = new SpawnState();
    private _spawnY = 0;
    private _mover = new SphereMover(CAMERA_RADIUS);

    set collision(value: Collision | null) {
        this._mover.collision = value;
        this._mover.reset(this._position);
    }

    get collision(): Collision | null {
        return this._mover.collision;
    }

    onEnter(state: CameraState): void {
        this.goto(state);
        if (this.collision &&
            findSphereSpawn(this.collision, this._position.x, this._position.y, this._position.z,
                CAMERA_RADIUS, spawnProbe)) {
            this._position.copy(spawnProbe);
            this._mover.reset(this._position);
        }
        this._storeSpawn();
        this._spawnY = this._position.y;
    }

    update(deltaTime: number, inputFrame: CameraFrame, state: CameraState) {
        const { move, rotate } = inputFrame.read();

        applyFrameRotation(this._targetAngles, rotate);
        dampAngles(this._angles, this._targetAngles, this.rotateDamping, deltaTime);

        this._step(deltaTime, move);

        state.position.copy(this._position);
        state.angles.set(this._angles.x, this._angles.y, 0);
        state.fov = this.fov;
    }

    onExit(_state: CameraState): void {
        // nothing
    }

    goto(state: CameraState) {
        this._position.copy(state.position);
        this._angles.set(state.angles.x, state.angles.y, 0);
        this._targetAngles.copy(this._angles);
        this._mover.reset(this._position);
    }

    resetToSpawn(state: CameraState): boolean {
        if (!this._spawn.has) return false;
        this._spawn.restore(this._position, this._angles);
        this._targetAngles.copy(this._angles);
        this._mover.reset(this._position);
        state.position.copy(this._position);
        state.angles.copy(this._angles);
        state.fov = this.fov;
        return true;
    }

    private _storeSpawn() {
        this._spawn.store(this._position, this._angles);
    }

    private _step(dt: number, move: number[]) {
        setCameraBasis(this._angles, forward, right, up);
        const s = this.moveSpeed * dt;
        setBasisOffset(offset, move[0] * s, move[1] * s, move[2] * s, forward, right, up);
        this._mover.move(this._position, offset);

        // 最低高度钳制：飞行不能把脚压进地面。向下短探针找正下方地表，
        // 眼睛高度不低于 地表 + flyFeetDrop（脚底贴地）。
        if (this.flyFeetDrop > 0 && this._mover.collision) {
            const hit = this._mover.collision.queryRay(
                this._position.x, this._position.y + FLOOR_PROBE_MARGIN, this._position.z,
                0, -1, 0, this.flyFeetDrop + FLOOR_PROBE_MARGIN + FLOOR_PROBE_SKIN);
            if (hit) {
                const minY = hit.y + this.flyFeetDrop;
                if (this._position.y < minY) this._position.y = minY;
            }
        }

        // Clamp altitude: player cannot fly higher than flyMaxHeight above spawn.
        if (this.holdEntryAltitude) {
            // 进场飞行期：高度保持与场景设定的初始位置一致（垂直位移全抵消）。
            this._position.y = this._spawnY;
        } else {
            const maxY = this._spawnY + this.flyMaxHeight;
            if (this._position.y > maxY) this._position.y = maxY;
        }
    }
}

export { FirstPersonController };
